import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  FileText,
  FileSpreadsheet,
  Presentation,
  Minus,
  Plus,
} from 'lucide-react';
import { FileTypeIcon } from '@/components/ui/file-type-icon';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { LoadingDocument, LoadingSpinner } from '@/components/ui/loading-preview';
import * as docxPreview from 'docx-preview';
import * as XLSX from 'xlsx';

const DOCX_PREVIEW_CSS = `
  .docx-preview-wrapper {
    background: white;
    padding: 4px 4px 12px;
    min-height: 100%;
  }
  .docx-preview-wrapper > section.docx {
    background: white;
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.06);
    margin: 4px auto;
    padding: 16px 20px;
    max-width: 100%;
    overflow: visible;
  }
  .docx-preview-wrapper * {
    font-family: inherit;
  }
  @media print {
    .docx-preview-wrapper { padding: 0; }
    .docx-preview-wrapper > section.docx {
      box-shadow: none;
      margin: 0;
      padding: 0;
    }
  }
`;

const XLSX_PREVIEW_CSS = `
  .xlsx-preview-table table {
    border-collapse: collapse;
    font-size: 13px;
  }
  .xlsx-preview-table td,
  .xlsx-preview-table th {
    border: 1px solid #e2e8f0;
    padding: 6px 10px;
    text-align: left;
  }
  .xlsx-preview-table th {
    background: #f1f5f9;
    font-weight: 600;
  }
`;

type FileCategory = 'pdf' | 'office-word' | 'office-excel' | 'office-powerpoint' | 'html' | 'image' | 'unknown';

function getFileCategory(type: string, name: string): FileCategory {
  const fileType = type.toLowerCase();
  const fileName = name.toLowerCase();

  if (fileType === 'pdf' || fileName.endsWith('.pdf')) {
    return 'pdf';
  }
  if (fileType === 'doc' || fileType === 'docx' || fileName.match(/\.(doc|docx)$/)) {
    return 'office-word';
  }
  if (
    fileType === 'xls' ||
    fileType === 'xlsx' ||
    fileType === 'excel' ||
    fileType === 'csv' ||
    fileName.match(/\.(xls|xlsx|csv)$/)
  ) {
    return 'office-excel';
  }
  if (fileType === 'ppt' || fileType === 'pptx' || fileName.match(/\.(ppt|pptx)$/)) {
    return 'office-powerpoint';
  }
  if (fileType === 'html' || fileType === 'txt' || fileName.match(/\.(html|txt)$/)) {
    return 'html';
  }
  if (
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileType) ||
    fileName.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)
  ) {
    return 'image';
  }
  return 'unknown';
}

function getLoadingTextForCategory(category: FileCategory): string {
  switch (category) {
    case 'pdf':
      return 'Loading PDF...';
    case 'office-word':
      return 'Loading document...';
    case 'office-excel':
      return 'Loading spreadsheet...';
    case 'office-powerpoint':
      return 'Loading presentation...';
    case 'html':
      return 'Loading content...';
    case 'image':
      return 'Loading image...';
    default:
      return 'Loading preview...';
  }
}

function getIconForCategory(category: FileCategory) {
  switch (category) {
    case 'office-excel':
      return FileSpreadsheet;
    case 'office-powerpoint':
      return Presentation;
    default:
      return FileText;
  }
}

interface FileErrorStateProps {
  error: string;
  onDownload: () => void;
}

function FileErrorState({ error, onDownload }: FileErrorStateProps) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white">
      <AlertCircle size={32} className="mb-2 text-red-400" />
      <p className="mb-4 max-w-sm text-center text-slate-600">{error}</p>
      <Button onClick={onDownload} variant="outline">
        Download file
      </Button>
    </div>
  );
}

const DOCX_ZOOM_MIN = 0.5;
const DOCX_ZOOM_MAX = 2;
const DOCX_ZOOM_STEP = 0.1;

interface DocxPreviewProps {
  url: string;
  className?: string;
  onLoad?: () => void;
  onError?: (error: string) => void;
}

function DocxPreviewRenderer({ url, className, onLoad, onError }: DocxPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const styleContainerRef = useRef<HTMLDivElement>(null);
  const [isRendering, setIsRendering] = useState(true);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setZoom(1);
  }, [url]);

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const renderDocument = async () => {
      if (!containerRef.current) {
        setTimeout(() => {
          if (isMounted) void renderDocument();
        }, 50);
        return;
      }

      setIsRendering(true);
      setRenderError(null);
      containerRef.current.innerHTML = '';

      try {
        const response = await fetch(url, { signal: abortController.signal });

        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }

        const blob = await response.blob();

        if (!isMounted || !containerRef.current) {
          return;
        }

        containerRef.current.innerHTML = '';

        await docxPreview.renderAsync(blob, containerRef.current, styleContainerRef.current ?? undefined, {
          className: 'docx-preview',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: true,
          trimXmlDeclaration: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          debug: false,
        });

        if (isMounted) {
          setIsRendering(false);
          onLoad?.();
        }
      } catch (error: unknown) {
        const err = error as { name?: string; message?: string };
        if (err?.name === 'AbortError') return;

        if (isMounted) {
          console.error('[DocxPreview] Error rendering document:', error);
          const errorMessage = err?.message || 'Failed to render document';
          setRenderError(errorMessage);
          setIsRendering(false);
          onError?.(errorMessage);
        }
      }
    };

    void renderDocument();

    return () => {
      isMounted = false;
      abortController.abort();
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [url, onLoad, onError]);

  const zoomOut = () =>
    setZoom((z) => Math.max(DOCX_ZOOM_MIN, Math.round((z - DOCX_ZOOM_STEP) * 10) / 10));
  const zoomIn = () =>
    setZoom((z) => Math.min(DOCX_ZOOM_MAX, Math.round((z + DOCX_ZOOM_STEP) * 10) / 10));
  const zoomReset = () => setZoom(1);

  const showZoomToolbar = !renderError;

  return (
    <div className={cn('relative flex h-full w-full min-h-0 flex-col bg-white', className)}>
      <style>{DOCX_PREVIEW_CSS}</style>
      <div ref={styleContainerRef} className="hidden" />

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {showZoomToolbar && (
          <div
            className="pointer-events-none absolute right-1 top-1 z-20 flex items-center gap-0.5 rounded-md border border-slate-200/80 bg-white/95 px-1 py-0.5 shadow-sm backdrop-blur-[2px]"
            aria-label="Zoom controls"
          >
            <span className="pointer-events-auto mr-0.5 pl-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
              Zoom
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="pointer-events-auto h-6 w-6 p-0"
              disabled={isRendering || zoom <= DOCX_ZOOM_MIN}
              onClick={zoomOut}
              title="Zoom out"
              aria-label="Zoom out"
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <span className="pointer-events-auto min-w-[2.25rem] text-center text-[10px] tabular-nums text-slate-600">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="pointer-events-auto h-6 w-6 p-0"
              disabled={isRendering || zoom >= DOCX_ZOOM_MAX}
              onClick={zoomIn}
              title="Zoom in"
              aria-label="Zoom in"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="pointer-events-auto h-6 px-1.5 text-[10px] leading-none"
              disabled={isRendering || zoom === 1}
              onClick={zoomReset}
              title="Reset zoom to 100%"
            >
              Reset
            </Button>
          </div>
        )}

        {isRendering && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoadingDocument text="Rendering document..." icon={FileText} />
          </div>
        )}

        {renderError && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
            <AlertCircle size={32} className="mb-2 text-amber-400" />
            <p className="mb-2 max-w-sm text-center text-sm text-slate-600">Could not render preview locally</p>
            <p className="mb-4 max-w-sm text-center text-xs text-slate-400">{renderError}</p>
          </div>
        )}

        <div
          className={cn('min-h-0 flex-1 overflow-y-auto overflow-x-auto overscroll-contain', isRendering && 'invisible')}
        >
          <div
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              width: `${100 / zoom}%`,
            }}
          >
            <div ref={containerRef} className="w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

interface ExcelPreviewProps {
  url: string;
  className?: string;
  onLoad?: () => void;
  onError?: (error: string) => void;
}

function ExcelPreviewRenderer({ url, className, onLoad, onError }: ExcelPreviewProps) {
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [htmlBySheet, setHtmlBySheet] = useState<string[]>([]);
  const [isRendering, setIsRendering] = useState(true);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    let isMounted = true;

    const renderSpreadsheet = async () => {
      setIsRendering(true);
      setRenderError(null);
      setActiveSheetIndex(0);
      setSheetNames([]);
      setHtmlBySheet([]);

      try {
        const response = await fetch(url, { signal: abortController.signal });
        if (!response.ok) throw new Error(`Failed to fetch spreadsheet: ${response.status}`);

        const arrayBuffer = await response.arrayBuffer();
        if (!isMounted) return;

        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const names = workbook.SheetNames;
        if (!names.length) throw new Error('No sheets found in workbook');

        const htmls = names.map((name) => {
          const ws = workbook.Sheets[name];
          if (!ws) {
            return '<table><tbody><tr><td class="empty-sheet">Empty Excel</td></tr></tbody></table>';
          }
          try {
            return XLSX.utils.sheet_to_html(ws, { id: `sheet-${name}`, editable: false });
          } catch {
            return '<table><tbody><tr><td class="empty-sheet">Empty Excel</td></tr></tbody></table>';
          }
        });

        if (isMounted) {
          setSheetNames(names);
          setHtmlBySheet(htmls);
          setIsRendering(false);
          onLoad?.();
        }
      } catch (err: unknown) {
        if ((err as Error)?.name === 'AbortError') return;
        if (isMounted) {
          const msg = err instanceof Error ? err.message : 'Failed to render spreadsheet';
          console.error('[ExcelPreview] Error:', err);
          setRenderError(msg);
          setIsRendering(false);
          onError?.(msg);
        }
      }
    };

    void renderSpreadsheet();
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [url, onLoad, onError]);

  return (
    <div className={cn('relative flex h-full w-full flex-col bg-slate-100', className)}>
      <style>{XLSX_PREVIEW_CSS}</style>
      {isRendering && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
          <LoadingDocument text="Rendering spreadsheet..." icon={FileSpreadsheet} />
        </div>
      )}
      {renderError && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
          <AlertCircle size={32} className="mb-2 text-amber-400" />
          <p className="mb-2 max-w-sm text-center text-sm text-slate-600">Could not render spreadsheet locally</p>
          <p className="mb-4 max-w-sm text-center text-xs text-slate-400">{renderError}</p>
        </div>
      )}
      {!isRendering && !renderError && htmlBySheet.length > 0 && (
        <>
          {sheetNames.length > 1 && (
            <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 bg-white p-2">
              {sheetNames.map((name, idx) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => setActiveSheetIndex(idx)}
                  className={cn(
                    'whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors',
                    idx === activeSheetIndex
                      ? 'bg-indigo-100 font-medium text-indigo-800'
                      : 'text-slate-600 hover:bg-slate-100'
                  )}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-auto bg-white p-4">
            <div
              className="xlsx-preview-table overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: htmlBySheet[activeSheetIndex] ?? '' }}
            />
          </div>
        </>
      )}
    </div>
  );
}

export interface FilePreviewProps {
  url: string;
  name: string;
  type: string;
  className?: string;
  onDownload?: () => void;
  htmlContent?: string;
}

export function FilePreview({ url, name, type, className, onDownload, htmlContent }: FilePreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingRef = useRef(true);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const category = getFileCategory(type, name);
  const loadingText = getLoadingTextForCategory(category);
  const LoadingIcon = getIconForCategory(category);

  const cleanup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  };

  const markLoaded = () => {
    setIsLoading(false);
    isLoadingRef.current = false;
    cleanup();
  };

  const markError = (message: string) => {
    setError(message);
    setIsLoading(false);
    isLoadingRef.current = false;
    cleanup();
  };

  useEffect(() => {
    if (!url || url.trim() === '') {
      markError('Invalid file URL');
      return undefined;
    }

    try {
      new URL(url);
    } catch {
      markError('Invalid file URL');
      return undefined;
    }

    setIsLoading(true);
    isLoadingRef.current = true;
    setError(null);
    cleanup();

    timeoutRef.current = setTimeout(() => {
      if (isLoadingRef.current) {
        markError('The file is taking too long to load.');
      }
    }, 20000);

    return cleanup;
  }, [url, type, name]);

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (category === 'html') {
    if (htmlContent) {
      return (
        <div className={cn('h-full w-full overflow-auto bg-white p-8', className)}>
          <div
            className="mx-auto max-w-none text-sm leading-relaxed text-slate-800 [&_a]:text-primary [&_code]:rounded [&_code]:bg-slate-100 [&_pre]:overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </div>
      );
    }

    return (
      <div className={cn('relative h-full w-full bg-white', className)}>
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoadingSpinner size={32} className="text-primary" />
          </div>
        )}
        {error && <FileErrorState error={error} onDownload={handleDownload} />}
        <iframe
          ref={iframeRef}
          src={url}
          className="block h-full w-full border-0"
          onLoad={markLoaded}
          onError={() => markError('Error loading content')}
          title={name}
          sandbox="allow-same-origin allow-scripts"
        />
      </div>
    );
  }

  if (category === 'pdf') {
    const pdfUrl = url.includes('#') ? url : `${url}#toolbar=1&navpanes=0&scrollbar=1`;

    return (
      <div className={cn('relative flex h-full w-full flex-col bg-slate-100', className)}>
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoadingDocument text={loadingText} icon={LoadingIcon} />
          </div>
        )}
        {error && <FileErrorState error={error} onDownload={handleDownload} />}
        <iframe
          src={pdfUrl}
          className="block h-full w-full border-0"
          onLoad={markLoaded}
          onError={() => setIsLoading(false)}
          title={name}
        />
      </div>
    );
  }

  if (category === 'office-word') {
    const isDocx = name.toLowerCase().endsWith('.docx') || type.toLowerCase() === 'docx';

    if (isDocx) {
      return (
        <DocxPreviewRenderer
          url={url}
          className={className}
          onLoad={markLoaded}
          onError={(err) => markError(err)}
        />
      );
    }

    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

    const handleOfficeLoad = () => {
      let attempts = 0;
      const maxAttempts = 4;

      checkIntervalRef.current = setInterval(() => {
        attempts++;
        if (attempts >= maxAttempts) {
          markLoaded();
        }
      }, 500);
    };

    return (
      <div className={cn('relative flex h-full w-full flex-col bg-slate-100', className)}>
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoadingDocument text={loadingText} icon={LoadingIcon} />
          </div>
        )}
        {error && <FileErrorState error={error} onDownload={handleDownload} />}
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          className="block h-full w-full border-0"
          onLoad={handleOfficeLoad}
          onError={() => markError('Error loading document. The file may not be publicly accessible.')}
          title={name}
        />
      </div>
    );
  }

  if (category === 'office-excel') {
    return (
      <ExcelPreviewRenderer
        url={url}
        className={className}
        onLoad={markLoaded}
        onError={(err) => markError(err)}
      />
    );
  }

  if (category === 'office-powerpoint') {
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

    const handleOfficeLoad = () => {
      let attempts = 0;
      const maxAttempts = 4;

      checkIntervalRef.current = setInterval(() => {
        attempts++;
        if (attempts >= maxAttempts) {
          markLoaded();
        }
      }, 500);
    };

    return (
      <div className={cn('relative flex h-full w-full flex-col bg-slate-100', className)}>
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoadingDocument text={loadingText} icon={LoadingIcon} />
          </div>
        )}
        {error && <FileErrorState error={error} onDownload={handleDownload} />}
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          className="block h-full w-full border-0"
          onLoad={handleOfficeLoad}
          onError={() => markError('Error loading document. The file may not be publicly accessible.')}
          title={name}
        />
      </div>
    );
  }

  if (category === 'image') {
    return (
      <div className={cn('relative flex h-full w-full items-center justify-center bg-slate-100', className)}>
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoadingSpinner size={32} className="text-primary" />
          </div>
        )}
        {error && <FileErrorState error={error} onDownload={handleDownload} />}
        <img
          src={url}
          alt={name}
          className="max-h-full max-w-full object-contain"
          onLoad={markLoaded}
          onError={() => markError('Error loading image')}
        />
      </div>
    );
  }

  return (
    <div className={cn('flex h-full flex-col items-center justify-center bg-white p-6', className)}>
      <FileTypeIcon type="generic" size={48} className="mb-3 text-slate-300" />
      <p className="mb-1 text-sm font-medium text-slate-600">Preview not available</p>
      <p className="mb-4 text-center text-xs text-slate-400">This file type cannot be viewed directly.</p>
      <Button onClick={handleDownload} variant="outline" size="sm" className="gap-2">
        <Download size={14} />
        Download file
      </Button>
    </div>
  );
}

export interface FileViewerProps {
  file: {
    id: string;
    name: string;
    url: string;
    type: string;
    size?: string;
  };
  onClose?: () => void;
  onDownload?: (file: { id: string; name: string; url: string }) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  className?: string;
}

export function FileViewer({
  file,
  onClose,
  onDownload,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  className,
}: FileViewerProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
      if (e.key === 'ArrowLeft' && hasPrevious && onPrevious) onPrevious();
      if (e.key === 'ArrowRight' && hasNext && onNext) onNext();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onPrevious, onNext, hasPrevious, hasNext]);

  if (!mounted) return null;

  const handleDownloadWrapper = () => {
    if (onDownload) {
      onDownload(file);
    } else {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const content = (
    <div className="animate-in fade-in fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px] duration-200 sm:p-6">
      <div
        className={cn(
          'flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl animate-in zoom-in-95 duration-200',
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-100 bg-slate-50/50 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {(hasPrevious || hasNext) && (
              <div className="mr-1 flex flex-shrink-0 items-center gap-1 border-r border-slate-200 pr-3">
                <button
                  type="button"
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  className={cn(
                    'rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700',
                    !hasPrevious && 'cursor-not-allowed opacity-30'
                  )}
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={!hasNext}
                  className={cn(
                    'rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700',
                    !hasNext && 'cursor-not-allowed opacity-30'
                  )}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}

            <div className="flex-shrink-0 rounded-lg border border-slate-100 bg-white p-1.5 shadow-sm">
              <FileTypeIcon type={file.type} size={18} />
            </div>

            <div className="flex min-w-0 flex-col">
              <h3 className="truncate text-sm font-semibold text-slate-800" title={file.name}>
                {file.name}
              </h3>
              {file.size ? <p className="text-[10px] font-medium text-slate-500">{file.size}</p> : null}
            </div>
          </div>

          <div className="flex items-center gap-2 pl-4">
            <button
              type="button"
              onClick={handleDownloadWrapper}
              className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-primary/5 hover:text-primary"
              title="Download"
            >
              <Download size={18} />
            </button>
            <div className="mx-1 h-6 w-px bg-slate-200" />
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
              title="Close (Esc)"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1 bg-slate-100">
          <FilePreview url={file.url} name={file.name} type={file.type} onDownload={handleDownloadWrapper} />
        </div>
      </div>

      <div className="absolute inset-0 -z-10" onClick={onClose} />
    </div>
  );

  return createPortal(content, document.body);
}
